/**
 * @name: Homepage Hero Banner Media Loader
 * @author: Cursor (Agent)
 * @date: 2026-07-22
 * @description: Swaps small placeholder images for full-size when slides are visible.
 * @dependencies: jQuery, Slick (custom.js)
 * @strict-mode: true
 */

(function () {
  const SELECTORS = {
    slider: '.section_hero-banner .img-text-card-wrapper',
    slide: '.card-img-text',
    mediaRoot: '[data-hero-media-item]',
    deferredImage: 'img[data-deferred-image]',
    deferredVideo: 'video[data-deferred-video]',
  };

  /**
   * Approximate visible slide count before Slick init (matches custom.js breakpoints).
   */
  const getVisibleSlideCount = () => {
    const width = window.innerWidth;

    if (width >= 2400) return 6;
    if (width >= 1921) return 4;
    if (width >= 1700) return 5;
    if (width >= 1300) return 3;
    if (width >= 768) return 3;
    if (width >= 600) return 2;

    return 2;
  };

  /**
   * Preload full image, then swap src so the small placeholder stays until ready.
   */
  const loadImage = (img) => {
    const fullSrc = img.getAttribute('data-src');
    const fullSrcset = img.getAttribute('data-srcset');

    if (!fullSrc && !fullSrcset) return;

    const applyFullSources = () => {
      if (fullSrcset) {
        img.setAttribute('srcset', fullSrcset);
        img.removeAttribute('data-srcset');
      }

      if (fullSrc) {
        img.setAttribute('src', fullSrc);
        img.removeAttribute('data-src');
      }

      img.removeAttribute('data-deferred-image');
    };

    const preload = new Image();
    const sizes = img.getAttribute('sizes');

    if (sizes) preload.sizes = sizes;
    if (fullSrcset) preload.srcset = fullSrcset;
    if (fullSrc) preload.src = fullSrc;

    preload.onload = applyFullSources;
    preload.onerror = applyFullSources;

    if (preload.complete) applyFullSources();
  };

  const loadVideo = (video) => {
    const poster = video.getAttribute('data-poster');
    if (poster) video.setAttribute('poster', poster);

    for (const source of video.querySelectorAll('source[data-src]')) {
      const deferredSrc = source.getAttribute('data-src');
      if (deferredSrc) source.setAttribute('src', deferredSrc);
    }

    video.load();
    video.removeAttribute('data-deferred-video');

    if (video.hasAttribute('data-autoplay-on-hydrate')) {
      video.play().catch(() => {});
    }
  };

  const loadSlideMedia = (root) => {
    if (!root || root.dataset.mediaLoaded === 'true') return;

    const img = root.querySelector(SELECTORS.deferredImage);
    if (img instanceof HTMLImageElement) loadImage(img);

    const video = root.querySelector(SELECTORS.deferredVideo);
    if (video instanceof HTMLVideoElement) loadVideo(video);

    root.dataset.mediaLoaded = 'true';
  };

  /**
   * Fallback when Slick has not initialized yet (theme editor / slow JS).
   */
  const loadPreSlickSlides = (wrapper) => {
    const slides = wrapper.querySelectorAll(SELECTORS.slide);
    const visibleCount = getVisibleSlideCount();

    for (let index = 0; index < visibleCount && index < slides.length; index++) {
      loadSlideMedia(slides[index].querySelector(SELECTORS.mediaRoot));
    }
  };

  const loadVisibleSlides = (wrapper) => {
    if (!wrapper.classList.contains('slick-initialized')) return;
    if (typeof jQuery === 'undefined') return;

    const slick = jQuery(wrapper).slick('getSlick');
    if (!slick) return;

    const startIndex = slick.currentSlide || 0;
    const visibleCount = Math.ceil(slick.options.slidesToShow || 1);

    for (let index = startIndex; index < startIndex + visibleCount; index++) {
      const slide = slick.$slides.get(index);
      if (!slide) continue;

      loadSlideMedia(slide.querySelector(SELECTORS.mediaRoot));
    }
  };

  const bindSlider = (wrapper) => {
    if (typeof jQuery === 'undefined') return;

    const $wrapper = jQuery(wrapper);

    $wrapper.off('init.heroMedia afterChange.heroMedia');
    $wrapper.on('init.heroMedia afterChange.heroMedia', () => {
      loadVisibleSlides(wrapper);
    });

    if (wrapper.classList.contains('slick-initialized')) {
      loadVisibleSlides(wrapper);
    } else {
      loadPreSlickSlides(wrapper);
    }
  };

  const init = () => {
    const wrapper = document.querySelector(SELECTORS.slider);
    if (!wrapper) return;

    bindSlider(wrapper);
  };

  if (document.readyState === 'interactive' || document.readyState === 'complete') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }
  document.addEventListener('shopify:section:load', init);
})();
